// useEventDraft — the AddEventForm form/draft state machine.
//
// Extracted verbatim from `components/AddEventForm.js` (behavior-preserving
// refactor, issue #240). This hook owns the modal's editable draft and the
// validation-error map, EXCEPT the submit flow (see `useSaveEvent`):
//
//   State:
//     - formData (seeded once via getInitialFormData() — a fresh flat object
//       with every field for every event type defaulted to '')
//     - errors   (the validation-error map: { [field]: 'This field is required' })
//
//   Handler (params + side effects preserved EXACTLY):
//     - handleChange(field, value) — the source's `handleInputChange`: writes
//       `value` into `formData[field]`, then clears that field's validation
//       error if one is present. The source only called `setValidationErrors`
//       when `validationErrors[field]` was truthy; the functional updater here
//       returns the SAME reference when the field has no error, so React bails
//       out and no extra re-render occurs — preserving the source's
//       error-clear-on-edit behavior exactly while keeping the callback stable.
//
//   Derived:
//     - eventTypeLabel — getEventTypeLabel(eventType), the label the source
//       rendered in the modal title and the Save button.
//
// `errors`/`setErrors` are exposed so the submit flow (`useSaveEvent`) can set
// the validation map and the slimmed container (task 13.7) can render per-field
// errors. The hook reuses the 13.2 logic modules (getInitialFormData,
// getEventTypeLabel) rather than duplicating them.
//
// `use`-prefixed, contains no JSX and performs no rendering.
//
// Validates: Requirements 3.2, 3.4, 1.4

import { useState, useCallback } from 'react';

import getInitialFormData from '../logic/getInitialFormData';
import getEventTypeLabel from '../logic/getEventTypeLabel';

/**
 * Owns the AddEventForm draft (form values) and the validation-error map.
 *
 * @param {Object} params
 * @param {string} params.eventType - The active event type; selects the
 *   derived display label (and keys validation in the submit flow).
 * @returns {{
 *   formData: Object,
 *   setFormData: import('react').Dispatch<import('react').SetStateAction<Object>>,
 *   errors: Object,
 *   setErrors: import('react').Dispatch<import('react').SetStateAction<Object>>,
 *   handleChange: (field: string, value: *) => void,
 *   eventTypeLabel: string
 * }} The draft API consumed by the container and the submit hook.
 */
export function useEventDraft({ eventType }) {
  const [formData, setFormData] = useState(getInitialFormData);
  const [errors, setErrors] = useState({});

  // ── Write a field, then clear that field's error if one is present ──────────
  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Mirror the source: only mutate the error map when this field has an error.
    // Returning the same reference when it doesn't keeps React from re-rendering,
    // matching the source's guarded `if (validationErrors[field])` call.
    setErrors(prev => {
      if (!prev[field]) return prev;
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  }, []);

  // Derived display label (modal title + Save button), matching the source.
  const eventTypeLabel = getEventTypeLabel(eventType);

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    handleChange,
    eventTypeLabel,
  };
}

export default useEventDraft;
